import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, CreditCard, Lock, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const mode = params.get("mode") === "buy" ? "buy" : "rent";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [film, setFilm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState("stripe"); // "stripe" | "mock"
  const [card, setCard] = useState({ name: "Demo User", number: "4242 4242 4242 4242", exp: "12/29", cvc: "123" });

  useEffect(() => {
    document.body.classList.add("theme-mini");
    return () => document.body.classList.remove("theme-mini");
  }, []);

  useEffect(() => {
    api.get(`/films/${slug}`).then(({ data }) => setFilm(data));
  }, [slug]);

  useEffect(() => {
    if (user === false) navigate(`/login?redirect=/film/${slug}/checkout?mode=${mode}`);
  }, [user, navigate, slug, mode]);

  if (!film) return <div className="min-h-screen bg-gs-screen text-white flex items-center justify-center font-mono text-xs uppercase tracking-widest">Loading…</div>;

  const amount = mode === "buy" ? film.buy_price : film.rent_price;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (provider === "stripe") {
        const { data } = await api.post(`/films/${slug}/stripe-checkout`, {
          mode,
          origin_url: window.location.origin,
        });
        window.location.href = data.url;
        return;
      }
      const last4 = card.number.replace(/\s/g, "").slice(-4) || "4242";
      await api.post(`/films/${slug}/checkout`, { mode, card_name: card.name, card_last4: last4 });
      toast.success(mode === "buy" ? "Owned. Welcome to the theater." : "Ticket purchased. Enjoy the show.");
      navigate(`/film/${slug}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="film-grain min-h-screen bg-gs-screen text-white">
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to={`/film/${slug}`} className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70 hover:text-gs-neon inline-flex items-center gap-2">
            <ArrowLeft size={12} /> Back to the theater
          </Link>
          <div className="flex items-center gap-3">
            <img src="/brand/monogram.png" alt="" className="h-7 w-auto" />
            <p className="font-display text-xl tracking-wide">BOX OFFICE</p>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-2 gap-12">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-neon">Your ticket</p>
          <h1 className="font-display text-5xl tracking-wide mt-3">{film.title.toUpperCase()}</h1>
          <p className="font-serif italic text-xl text-white/70 mt-2">{film.tagline}</p>

          <div className="mt-10 border border-white/15 p-6 space-y-4 font-mono text-xs uppercase tracking-widest">
            <div className="flex items-center justify-between">
              <span className="text-white/50">Mode</span>
              <span>{mode === "buy" ? "Buy · keep forever" : "Rent · 48 hours"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/50">Subtotal</span>
              <span>${amount.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/50">Tax</span>
              <span>$0.00</span>
            </div>
            <div className="border-t border-white/15 pt-4 flex items-center justify-between text-base">
              <span className="text-white">Total</span>
              <span className="text-gs-neon">${amount.toFixed(2)}</span>
            </div>
          </div>

          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-6 inline-flex items-center gap-2">
            <Lock size={10} /> Secure checkout · test mode
          </p>
        </div>

        <form onSubmit={submit} data-testid="checkout-form" className="border border-white/15 p-8 bg-white/[0.02]">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-neon mb-6 inline-flex items-center gap-2">
            <CreditCard size={12} /> Payment method
          </p>

          <div className="grid grid-cols-2 gap-3 mb-6">
            <button
              type="button"
              data-testid="provider-stripe-btn"
              onClick={() => setProvider("stripe")}
              className={`px-4 py-3 border font-mono text-[10px] uppercase tracking-widest transition-all ${provider === "stripe" ? "border-gs-neon text-white bg-gs-neon/10" : "border-white/20 text-white/60 hover:border-white/40"}`}
            >
              <Sparkles size={12} className="inline mr-2" /> Stripe (real)
            </button>
            <button
              type="button"
              data-testid="provider-mock-btn"
              onClick={() => setProvider("mock")}
              className={`px-4 py-3 border font-mono text-[10px] uppercase tracking-widest transition-all ${provider === "mock" ? "border-gs-neon text-white bg-gs-neon/10" : "border-white/20 text-white/60 hover:border-white/40"}`}
            >
              Mock (demo)
            </button>
          </div>

          {provider === "stripe" ? (
            <div className="space-y-4 font-mono text-xs text-white/70">
              <p>You'll be redirected to Stripe Checkout to complete payment securely.</p>
              <ul className="space-y-1 text-[10px] uppercase tracking-widest text-white/40">
                <li>· Test card: 4242 4242 4242 4242</li>
                <li>· Any future expiry · any CVC</li>
                <li>· No real charge in test mode</li>
              </ul>
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Name on card</label>
                <input data-testid="card-name-input" value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} className="w-full bg-transparent border border-white/20 px-4 py-3 font-mono text-sm focus:outline-none focus:border-gs-neon" required />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Card number</label>
                <input data-testid="card-number-input" value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} className="w-full bg-transparent border border-white/20 px-4 py-3 font-mono text-sm tracking-widest focus:outline-none focus:border-gs-neon" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Expiry</label>
                  <input data-testid="card-exp-input" value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value })} className="w-full bg-transparent border border-white/20 px-4 py-3 font-mono text-sm focus:outline-none focus:border-gs-neon" required />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">CVC</label>
                  <input data-testid="card-cvc-input" value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} className="w-full bg-transparent border border-white/20 px-4 py-3 font-mono text-sm focus:outline-none focus:border-gs-neon" required />
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="confirm-checkout-btn"
            className="mt-8 w-full bg-gs-ember text-gs-ink py-4 font-mono uppercase tracking-[0.25em] text-xs hover:bg-gs-cream transition-all shadow-[0_0_40px_rgba(201,169,97,0.3)] disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {busy ? "Processing…" : provider === "stripe" ? <><Sparkles size={16} /> Continue to Stripe · ${amount.toFixed(2)}</> : <><Check size={16} /> Confirm · ${amount.toFixed(2)}</>}
          </button>
        </form>
      </div>
    </div>
  );
}
